import { useId, useRef, useState } from "react";
import { canAdvance, canSkip, isAdvancing, isRedialing } from "@gioia365/domain/dialer-session";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { Button } from "../design/components/Button";
import { Icon } from "../design/components/Icon";
import { messageForError } from "../i18n/errors";
import { useT } from "../i18n/locale";
import { callsWord } from "../leads/card/sequence-texts";
import { useOptionalDialerSession } from "./dialer-session";
import { leadCardSearchOf } from "../tasks/task-search";
import { dialerLeadName, passoverText, sourceText } from "./dialer-texts";
import { useSourcePeople } from "./source-people";
import { NextLeadDialog } from "./NextLeadDialog";
import { QueueDialog } from "./QueueDialog";
import { SkipDialog } from "./SkipDialog";
import { SummaryDialog } from "./SummaryDialog";
import "./dialer.css";

// La barra «Power Dialer» del guscio (piano M5, Task 5; mock-up `scheda-lead.html`, `m-session`), sopra la barra della chiamata
// di M4: la sorgente, il Lead aperto, quanti fatti e quanti restano, chi viene dopo e lo stato; sotto, che cosa succede adesso
// (i secondi prima della chiamata, la pausa, il Lead che si salta da solo, l'esito da scegliere, i secondi prima del Lead
// successivo) e i comandi: «Chiama subito» (soltanto nel conto alla rovescia, sulla scheda), «Coda», «Pausa» / «Riprendi»,
// «Salta», «Interrompi», «Resta su questo Lead» e «Lead successivo», che si accende quando la postazione dice che i passi
// obbligatori sono fatti (ruling Q8); «Interrompi» aspetta lo stesso momento (ruling Q46). Dopo «Chiudi la chiamata», e per il
// Lead che si salta da solo, il Lead successivo arriva da solo (decisione del product owner del 9 ottobre 2026): la riga dice
// «Lead successivo fra 3…» (con un tentativo della sequenza a metà, che il passaggio lo chiude) e «Resta su questo Lead» toglie
// il conto; «Lead successivo» resta e avanza subito. In pausa su un Lead già fatto o che si salta da solo «Lead successivo» resta
// acceso e principale (`canAdvance`), la riga dice che riparte dal Lead dopo mentre «Riprendi» resta su questo (prova del 9
// ottobre 2026). Un comando spento dice perché (`aria-describedby`). Senza risposta, con «Senza risposta, richiama da solo…»
// acceso (TEL-32), mentre Gioia365 dà l'esito la riga lo dice («Senza risposta: Gioia365 segna «Nessuna risposta».») invece di
// chiederlo; poi il richiamo dello stesso Lead dice «Richiamo fra 3… (chiamata 2 di 4)», con «Chiama subito», «Pausa» e «Lead
// successivo», che con il tentativo a metà apre «Decidi tu»; «Salta» resta spento con il perché, come per ogni Lead che la vista
// dice già chiamato (`currentCalled`: il server lo rifiuterebbe). Fuori dalla scheda
// del Lead aperto, e per una sessione ritrovata, «Sessione in corso su …» con «Torna alla scheda». Senza sessione non c'è
// (resta soltanto il riepilogo di una sessione appena chiusa). A 390 px va a capo: il testo su una riga, i comandi sotto
// (`dialer.css`).

type Dialog = "queue" | "skip" | "next" | null;

export function DialerBar() {
  const t = useT();
  const dialer = useOptionalDialerSession();
  const navigate = useNavigate();
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const [dialog, setDialog] = useState<Dialog>(null);
  const statusId = useId();
  const calledId = useId();
  const interruptId = useId();
  const nextRef = useRef<HTMLButtonElement>(null);
  // Le persone che il nome della sorgente dei Task nomina (il collega di «Di chi»).
  const people = useSourcePeople(dialer?.tab ?? null, dialer?.session?.source ?? null);
  if (!dialer) return null;
  const { session, browser, summary } = dialer;
  const summaryDialog = summary ? <SummaryDialog shown={summary} /> : null;
  if (!session) return summaryDialog;

  const { current } = session;
  const phase = browser?.phase ?? null;
  // La fase da cui la pausa è partita: dice se il Lead aperto è già stato chiamato.
  const lead = browser?.phase === "paused" ? browser.pausedFrom : phase;
  const paused = phase === "paused" || session.state === "paused";
  // Il Lead è già stato chiamato: in chiamata, all'esito, fatto, nel richiamo dello stesso Lead (TEL-32), o come dice la vista
  // (`currentCalled`, anche dopo un richiamo che non è partito). «Salta» spento con il perché, «Lead successivo» con «Decidi tu» se il
  // tentativo è a metà.
  const redialing = browser !== null && isRedialing(browser);
  const called = lead === "calling" || lead === "outcome" || lead === "done" || redialing || session.currentCalled;
  const passed = lead === "passed";
  // Il Lead aperto è finito o si salta da solo: «Lead successivo» è il comando principale, anche in pausa (guarda la fase da cui la
  // pausa è partita, non `phase`), e in quel caso riprende la sessione prima di avanzare.
  const ready = lead === "done" || lead === "passed";
  const onCard = current !== null && pathname === `/lead/${encodeURIComponent(current.id)}`;
  const busy = dialer.busy;
  // I secondi prima del Lead successivo, sulla scheda e a sessione attiva, mai per la sessione ritrovata (che non passa da sola).
  // Allo zero il Lead successivo è già partito: la riga non dice «fra 0…».
  const advanceLeft =
    current !== null && browser !== null && isAdvancing(browser) && session.state === "active" && onCard && !dialer.restored ? browser.advanceLeft : null;
  const advancing = advanceLeft !== null && advanceLeft > 0;
  // Il tentativo della sequenza a metà che il passaggio chiude, come «Apri il Lead successivo» della finestra «Decidi tu».
  const closing = phase === "done" ? session.attemptPending : null;

  // Che cosa succede adesso: la riga sotto il titolo, che spiega anche i comandi spenti.
  let status: { text: string; live?: boolean } | null = null;
  if (advancing)
    status = {
      text: closing
        ? t("dialer.advancingAttempt", { seconds: advanceLeft, n: closing.attempt, made: callsWord(t, closing.made), required: closing.required })
        : t("dialer.advancing", { seconds: advanceLeft }),
      live: true,
    };
  else if (current !== null && passed && current.passover)
    status = {
      text: t("dialer.passedNow", {
        reason: passoverText(t, current.passover, dialer.timeZone),
      }),
    };
  else if (paused) status = { text: t(ready ? "dialer.pausedDone" : "dialer.pausedReason") };
  else if (current !== null && phase === "countdown" && (dialer.restored || !onCard))
    status = { text: t("dialer.restored", { source: sourceText(t, session, people) }) };
  else if (current !== null && phase === "countdown" && browser?.redial)
    status = { text: t("dialer.redialing", { seconds: browser.secondsLeft, n: browser.redial.call, required: browser.redial.of }), live: true };
  else if (current !== null && phase === "countdown" && browser)
    status = {
      text: t("dialer.countdown", {
        lead: dialerLeadName(current),
        seconds: browser.secondsLeft,
      }),
      live: true,
    };
  // Mentre Gioia365 dà l'esito (TEL-32) la riga non chiede di sceglierlo.
  else if (phase === "outcome") status = { text: t(dialer.awaitingOutcome !== null ? "dialer.noAnswerWaiting" : "dialer.beforeNext") };
  else if (current !== null && !onCard) status = { text: t("dialer.restored", { source: sourceText(t, session, people) }) };

  const advanceOk = !busy && (current === null ? session.state === "active" : browser !== null && canAdvance(browser));
  const skipOk = !busy && current !== null && browser !== null && canSkip(browser) && !session.currentCalled;
  const nextReason = advanceOk ? undefined : status && (paused || phase === "outcome") ? statusId : undefined;
  const skipReason = skipOk ? undefined : paused || passed ? statusId : called ? calledId : undefined;
  // «Interrompi» aspetta la postazione del Lead aperto, come «Lead successivo» (ruling Q46), con il perché.
  const blocked = dialer.interruptBlocked;
  const interruptOk = !busy && blocked === null;

  /** «Resta su questo Lead» sparisce con il clic: il focus passa a «Lead successivo», il comando che resta per passare a mano. */
  function stayHere() {
    dialer?.stay();
    nextRef.current?.focus();
  }

  function advance() {
    // Premuto durante il conto verso il Lead successivo, decide chi ha premuto: il conto si toglie, anche se poi nella finestra
    // «Decidi tu» sceglie «Resta su questa scheda».
    dialer?.stay();
    // R2: con un tentativo della sequenza ancora da finire, prima la domanda.
    if (session?.attemptPending && called) setDialog("next");
    else void dialer?.next({ passOver: false });
  }

  return (
    <section className="g-dialer-bar" aria-label={t("dialer.barLabel")} data-state={paused ? "paused" : "active"}>
      <div className="g-dialer-bar__info">
        <p className="g-dialer-bar__title">
          <Icon name="phone" />
          <span>
            {t("dialer.bar", {
              source: sourceText(t, session, people),
              lead: current ? dialerLeadName(current) : "—",
              done: session.done,
              remaining: session.remaining,
              next: session.next ? dialerLeadName(session.next) : t(session.source.type === "task_view" ? "dialer.nextNoneTasks" : "dialer.nextNone"),
            })}
          </span>
          <span aria-hidden="true">·</span>
          <span className="g-dialer-bar__state">{paused ? t("dialer.paused") : t("dialer.active")}</span>
        </p>
        {status ? (
          <p id={statusId} className="g-dialer-bar__status" role={status.live ? "status" : undefined}>
            {status.text}
          </p>
        ) : null}
        {called && !paused ? (
          <p id={calledId} className="g-dialer-bar__hint">
            {t("errors.dialer_already_called")}
          </p>
        ) : null}
        {blocked ? (
          <p id={interruptId} className="g-dialer-bar__hint">
            {blocked === "call" ? t("dialer.interruptInCall") : t("dialer.interruptBeforeOutcome")}
          </p>
        ) : null}
        {dialer.notice ? (
          <p className="g-dialer-bar__problem" role="alert">
            <Icon name="alert" />
            <span>{messageForError(t, dialer.notice)}</span>
          </p>
        ) : null}
      </div>
      <div className="g-dialer-bar__actions">
        {current !== null && !onCard ? (
          <Button
            onClick={() =>
              void navigate({
                to: "/lead/$leadId",
                params: { leadId: current.id },
                search: leadCardSearchOf(session.source),
              })
            }
          >
            {t("dialer.backToLead")}
          </Button>
        ) : null}
        {current !== null && onCard && phase === "countdown" ? (
          <Button variant="primary" disabled={busy} onClick={dialer.callNow}>
            <Icon name="phone" />
            {t("dialer.callNow")}
          </Button>
        ) : null}
        <Button aria-haspopup="dialog" onClick={() => setDialog("queue")}>
          <Icon name="smartlist" />
          {t("dialer.queue")}
        </Button>
        {paused ? (
          <Button disabled={busy} onClick={() => void dialer.resume()}>
            <Icon name="play" />
            {t("dialer.resume")}
          </Button>
        ) : (
          <Button disabled={busy} onClick={() => void dialer.pause()}>
            <Icon name="pause" />
            {t("dialer.pause")}
          </Button>
        )}
        <Button aria-haspopup="dialog" disabled={!skipOk} aria-describedby={skipReason} onClick={() => setDialog("skip")}>
          <Icon name="skip" />
          {t("dialer.skip")}
        </Button>
        <Button disabled={!interruptOk} aria-describedby={blocked ? interruptId : undefined} onClick={() => void dialer.interrupt().catch(() => undefined)}>
          <Icon name="stop" />
          {t("dialer.interrupt")}
        </Button>
        {advancing ? (
          <Button disabled={busy} onClick={stayHere}>
            {t("dialer.stayHere")}
          </Button>
        ) : null}
        <Button ref={nextRef} variant={ready ? "primary" : "secondary"} disabled={!advanceOk} aria-describedby={nextReason} onClick={advance}>
          {t("dialer.next")}
          <Icon name="right" />
        </Button>
      </div>
      {dialog === "queue" ? <QueueDialog onClose={() => setDialog(null)} /> : null}
      {dialog === "skip" ? <SkipDialog onClose={() => setDialog(null)} /> : null}
      {dialog === "next" && session.attemptPending ? <NextLeadDialog pending={session.attemptPending} onClose={() => setDialog(null)} /> : null}
      {summaryDialog}
    </section>
  );
}
